import { useState } from 'react'
import { X, Play, User, Key, Bug, Code } from 'lucide-react'
import type { AIService } from '@/lib/supabase-client'
import { buildServiceRequest, formatHeaders } from '@/lib/service-request-builder'
import CodeTemplateModal from './CodeTemplateModal'

interface ServiceDebugModalProps {
  service: AIService | null
  isOpen: boolean
  onClose: () => void
  testRequestBody: string
  setTestRequestBody: (value: string) => void
  testing: boolean
  testResult: string
  onTest: () => void
  testUsers: Array<{ id: string; email: string; name: string; balance: number }>
  selectedTestUser: { id: string; email: string; name: string; balance: number } | null
  testApiKeys: Array<{ id: string; key_value?: string; key_prefix?: string; name: string }>
  selectedApiKeyId: string
  selectedTestApiKey: string
  loadingTestUser: boolean
  onTestUserChange: (userId: string) => void
  onApiKeySelect: (apiKeyId: string) => void
  onTestApiKeyChange: (apiKey: string) => void
}

export function ServiceDebugModal({
  service,
  isOpen,
  onClose,
  testRequestBody,
  setTestRequestBody,
  testing,
  testResult,
  onTest,
  testUsers,
  selectedTestUser,
  testApiKeys,
  selectedApiKeyId,
  selectedTestApiKey,
  loadingTestUser,
  onTestUserChange,
  onApiKeySelect,
  onTestApiKeyChange
}: ServiceDebugModalProps) {
  const [codeTemplateOpen, setCodeTemplateOpen] = useState(false)

  if (!isOpen || !service) return null

  // 解析用户输入的参数
  let userParameters: Record<string, any> = {}
  let parseError: string | null = null
  try {
    userParameters = JSON.parse(testRequestBody || '{}')
  } catch (err) {
    parseError = err instanceof Error ? err.message : '无效的 JSON'
  }

  // 构建请求信息
  const requestInfo = selectedTestApiKey ? buildServiceRequest({
    provider: service.provider,
    serviceId: service.id, // 使用服务的 UUID
    userParameters
  }, selectedTestApiKey) : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Bug className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold">API 调试测试</h3>
              <p className="text-sm text-purple-100">{service.name} - {service.provider}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCodeTemplateOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
              title="下载代码模板"
            >
              <Code className="w-4 h-4" />
              <span className="text-sm font-medium">代码模板</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 测试用户选择 */}
          {loadingTestUser ? (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-sm text-blue-700">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              加载测试用户...
            </div>
          ) : testUsers.length > 0 && selectedTestUser ? (
            <div className="space-y-4">
              {/* 用户选择器 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline-block mr-1.5" />
                  选择测试用户
                </label>
                <select
                  value={selectedTestUser.id}
                  onChange={(e) => onTestUserChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition text-sm"
                >
                  {testUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.email || user.name} - 余额: {user.balance} 积分
                    </option>
                  ))}
                </select>
              </div>

              {/* API Key 选择器 */}
              {testApiKeys.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Key className="w-4 h-4 inline-block mr-1.5" />
                    选择 API Key
                  </label>
                  <select
                    value={selectedApiKeyId}
                    onChange={(e) => onApiKeySelect(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition text-sm"
                  >
                    {testApiKeys.map((key) => (
                      <option key={key.id} value={key.id}>
                        {key.name} {key.key_prefix ? `(${key.key_prefix}...)` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-blue-600">
                    ℹ️ 该用户有 {testApiKeys.length} 个 API Key
                  </p>
                </div>
              )}

              {/* API Key 输入 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Key className="w-4 h-4 inline-block mr-1.5" />
                  API Key（手动输入）
                </label>
                <input
                  type="text"
                  value={selectedTestApiKey}
                  onChange={(e) => onTestApiKeyChange(e.target.value)}
                  placeholder="请粘贴完整的 API Key（以 sk_ 开头）"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition text-sm font-mono"
                />
                <p className="mt-2 text-xs text-gray-500">
                  💡 测试 Key: <code className="bg-gray-100 px-2 py-0.5 rounded">sk_test_debug_key_for_development_only</code>
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  由于安全原因，数据库只存储 key 的哈希值，请手动输入完整的 API key。
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
              未找到测试用户，请先创建用户和 API Key
            </div>
          )}

          {/* 测试区域 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 左侧：参数输入 */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">测试参数 (JSON)</label>
                <textarea
                  value={testRequestBody}
                  onChange={(e) => setTestRequestBody(e.target.value)}
                  rows={20}
                  className={`w-full px-4 py-3 rounded-lg border ${parseError ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all font-mono text-sm resize-none`}
                  placeholder='{\n  "parameters": {\n    "input": "你好，这是测试"\n  }\n}'
                />
                {parseError && (
                  <p className="mt-1 text-xs text-red-600">{parseError}</p>
                )}
              </div>

              <button
                onClick={onTest}
                disabled={testing || !selectedTestApiKey || !!parseError}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
                title={
                  testing ? '测试中...' :
                  !selectedTestApiKey ? '请选择 API Key' :
                  parseError ? `JSON 格式错误: ${parseError}` :
                  '点击发送测试请求'
                }
              >
                {testing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    测试中...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    发送测试请求
                    {!selectedTestApiKey && <span className="text-xs opacity-75 ml-2">(请先选择 API Key)</span>}
                  </>
                )}
              </button>

              {/* 显示禁用原因 */}
              {!testing && (
                <div className="text-xs text-gray-500">
                  {!selectedTestApiKey && (
                    <p className="text-yellow-600">⚠️ 请先选择 API Key</p>
                  )}
                  {parseError && (
                    <p className="text-red-600">❌ JSON 格式错误: {parseError}</p>
                  )}
                  {selectedTestApiKey && !parseError && (
                    <p className="text-green-600">✓ 就绪，可以测试</p>
                  )}
                </div>
              )}
            </div>

            {/* 右侧：请求预览 */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg border border-gray-300 p-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  构建的请求预览
                </h5>
                {requestInfo ? (
                  <div className="space-y-3 text-xs font-mono">
                    <div>
                      <span className="text-gray-500 font-sans">URL:</span>
                      <div className="text-blue-600 break-all mt-1 bg-blue-50 p-2 rounded">
                        {requestInfo.method} {requestInfo.url}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500 font-sans">Headers:</span>
                      <pre className="bg-white p-2 rounded mt-1 text-[11px] overflow-x-auto whitespace-pre-wrap break-all border border-gray-200">
                        {formatHeaders(requestInfo.headers)}
                      </pre>
                    </div>
                    <div>
                      <span className="text-gray-500 font-sans">Body:</span>
                      <pre className="bg-white p-2 rounded mt-1 text-[11px] overflow-x-auto max-h-64 overflow-y-auto border border-gray-200">
                        {requestInfo.body}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-xs">
                    等待测试用户加载...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 响应结果（全宽） */}
          <div className="bg-gray-50 rounded-lg border border-gray-300 p-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${testResult ? 'bg-green-500' : 'bg-gray-300'}`} />
              服务器响应
            </h5>
            {testResult ? (
              <pre className="bg-gray-900 text-green-400 p-4 rounded text-[11px] font-mono max-h-[500px] overflow-auto">
                {testResult}
              </pre>
            ) : (
              <div className="text-center py-12 text-gray-400 text-xs">
                等待测试...
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            关闭
          </button>
        </div>
      </div>

      {/* Code Template Modal */}
      <CodeTemplateModal
        isOpen={codeTemplateOpen}
        onClose={() => setCodeTemplateOpen(false)}
        service={service}
        apiKey={selectedTestApiKey || ''}
      />
    </div>
  )
}
